import { useState } from "react";
import { Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";

export type ReportColumn<T> = {
  key: string;
  header: string;
  value: (record: T) => string | number | null | undefined;
};

/**
 * Construye la matriz (array of arrays) para la hoja de Excel, incluyendo solo
 * las columnas marcadas por el usuario y respetando el orden de `columns`.
 */
export function buildAoa<T>(
  records: T[],
  columns: ReportColumn<T>[],
  selected: Record<string, boolean>
): (string | number)[][] {
  const activeColumns = columns.filter((c) => selected[c.key]);
  const headers = activeColumns.map((c) => c.header);
  const rows = records.map((record) =>
    activeColumns.map((c) => {
      const value = c.value(record);
      return value === null || value === undefined ? "" : value;
    })
  );
  return [headers, ...rows];
}

type EntityReportFormProps<T> = {
  columns: ReportColumn<T>[];
  loadRecords: () => Promise<T[]>;
  fileName: string;
  sheetName?: string;
};

function EntityReportForm<T>(props: EntityReportFormProps<T>) {
  const { columns, loadRecords, fileName, sheetName = "Detalle" } = props;

  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    columns.reduce((acc, c) => ({ ...acc, [c.key]: true }), {})
  );
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const allSelected = columns.every((c) => selected[c.key]);

  const toggle = (key: string) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAll = () => {
    const next = !allSelected;
    setSelected(columns.reduce((acc, c) => ({ ...acc, [c.key]: next }), {}));
  };

  const generate = async () => {
    if (!columns.some((c) => selected[c.key])) {
      showAlert("warning", "Seleccione al menos un campo para el reporte");
      return;
    }

    setIsGenerating(true);
    try {
      const records = await loadRecords();
      const aoa = buildAoa(records, columns, selected);

      const sheet = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, sheetName);

      const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);
    } catch (error) {
      showAlert("danger", "Ocurrió un error al generar el reporte");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.container}>
      <p className={styles.hint}>Seleccione los campos a incluir en el reporte:</p>

      <Button color="link" className={styles.toggleAll} type="button" onClick={toggleAll}>
        {allSelected ? "Desmarcar todos" : "Marcar todos"}
      </Button>

      <div className={styles.fields}>
        {columns.map((c) => (
          <FormGroup check key={c.key} className={styles.field}>
            <Input
              type="checkbox"
              id={`report-field-${c.key}`}
              checked={!!selected[c.key]}
              onChange={() => toggle(c.key)}
            />
            <Label check for={`report-field-${c.key}`}>
              {c.header}
            </Label>
          </FormGroup>
        ))}
      </div>

      <Button
        color="primary"
        className={styles.generate}
        type="button"
        disabled={isGenerating}
        onClick={generate}
      >
        {isGenerating ? (
          <>
            <Spinner size="sm" /> Generando...
          </>
        ) : (
          "Generar Excel"
        )}
      </Button>
    </div>
  );
}

export default EntityReportForm;
